import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/lib/auth'
import { CommandPalette } from '@/components/CommandPalette'
import { useScheduledCatchUp } from '@/features/scheduled/hooks'

const NAV: Array<{ to: '/budget' | '/transactions' | '/accounts' | '/scheduled' | '/reports' | '/goals' | '/coach' | '/settings'; key: string }> = [
  { to: '/budget', key: 'nav.budget' },
  { to: '/transactions', key: 'nav.transactions' },
  { to: '/accounts', key: 'nav.accounts' },
  { to: '/scheduled', key: 'nav.scheduled' },
  { to: '/reports', key: 'nav.reports' },
  { to: '/goals', key: 'nav.goals' },
  { to: '/coach', key: 'nav.coach' },
  { to: '/settings', key: 'nav.settings' },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { signOut, user } = useAuth()
  useScheduledCatchUp()

  return (
    <div className="grid h-screen grid-cols-[220px_1fr] grid-rows-[56px_1fr]">
      <CommandPalette />
      <header className="col-span-2 flex items-center justify-between border-b border-zinc-200 px-4 dark:border-zinc-800">
        <span className="font-semibold">🦞 {t('app.name')}</span>
        <div className="flex items-center gap-3 text-sm text-zinc-500">
          {user?.email && <span className="hidden md:inline">{user.email}</span>}
          <button
            type="button"
            onClick={() => signOut()}
            className="rounded-md border border-zinc-300 px-2 py-1 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            {t('auth.signOut')}
          </button>
        </div>
      </header>

      <nav
        aria-label="Primary"
        className="border-r border-zinc-200 p-2 text-sm dark:border-zinc-800"
      >
        <ul className="space-y-1">
          {NAV.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="block rounded-md px-3 py-2 hover:bg-zinc-100 aria-[current=page]:bg-zinc-100 aria-[current=page]:font-medium dark:hover:bg-zinc-800 dark:aria-[current=page]:bg-zinc-800"
                activeProps={{ 'aria-current': 'page' }}
              >
                {t(item.key)}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <main className="overflow-auto p-6">{children}</main>
    </div>
  )
}
